// -------------------------------
// TASK INTERFACES

import { set } from "react-datepicker/dist/date_utils";

// -------------------------------
export interface PendingTask {
    Title?: string;
    TotalHours?: number;
    DueDate?: string;

    ProjectId?: number;
    ProjectLabel?: string;

    TeamMemberEmployeeId?: number[];
    Status?: number;            // TaskBoardId
    Priority?: string;          // High | Medium | Low
    Visibility?: string;        // Private | Public

    Tags?: string[];
    Description?: string;
    file?: File;
}

export interface PendingDeleteTask {
    awaitingName?: boolean;
}

export const startCreateProjectTaskFlow = (
    setPendingTask: (data: PendingTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    titleFromIntent?: string
) => {
    if (titleFromIntent) {
        setPendingTask({ Title: titleFromIntent });

        pushAIChat("How many total hours will this task take?");
        speak("How many total hours will this task take?");
        return;
    }

    setPendingTask({});
    pushAIChat("Sure! What is the task title?");
    speak("What is the task title?");
};

export const processPendingProjectTask = (
    text: string,
    pending: PendingTask,
    setPendingTask: (data: PendingTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // 1️⃣ TITLE
    if (!pending.Title) {
        const title = text.trim();
        if (!title) {
            pushAIChat("Please provide a valid task title.");
            speak("Please provide a valid task title.");
            return;
        }

        setPendingTask({ ...pending, Title: title });
        pushAIChat("How many total hours will this task take?");
        speak("How many total hours will this task take?");
        return;
    }

    // 2️⃣ TOTAL HOURS
    if (!pending.TotalHours) {
        const hours = Number(text);
        if (isNaN(hours)) {
            pushAIChat("Please enter a valid number of hours.");
            speak("Please enter a valid number of hours.");
            return;
        }

        setPendingTask({ ...pending, TotalHours: hours });
        pushAIChat("What is the due date?");
        speak("What is the due date?");
        return;
    }

    // 3️⃣ DUE DATE
    if (!pending.DueDate) {
        setPendingTask({ ...pending, DueDate: text });
        pushAIChat("Which project does this task belong to?");
        speak("Which project does this task belong to?");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "project" }
            })
        );
        return;
    }

    // 4️⃣ PROJECT (WAIT FOR OPTION SELECTION)
    if (!pending.ProjectId) {
        setPendingTask({ ...pending, ProjectLabel: text });
        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "team-member-project" }
            })
        );
        return;
    }


    // 5️⃣ TEAM MEMBERS (WAIT FOR OPTION SELECTION)
    if (!pending.TeamMemberEmployeeId || pending.TeamMemberEmployeeId.length === 0) {

        return;
    }


// 6️⃣ TASK BOARD
if (!pending.Status) {
    pushAIChat("Which task board should this task go to?");
    speak("Which task board should this task go to?");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "taskboard" }
        })
    );
    return;
}


    // 7️⃣ PRIORITY
    if (!pending.Priority) {
        const priority =
            text.toLowerCase().includes("high")
                ? "High"
                : text.toLowerCase().includes("medium")
                    ? "Medium"
                    : "Low";

        setPendingTask({ ...pending, Priority: priority });

        pushAIChat("Should this task be public or private?");
        speak("Should this task be public or private?");
        return;
    }

    // 8️⃣ VISIBILITY
    if (!pending.Visibility) {
        const visibility =
            text.toLowerCase().includes("public") ? "Public" : "Private";

        setPendingTask({ ...pending, Visibility: visibility });

        pushAIChat("Any description for this task?");
        speak("Any description for this task?");
        return;
    }

    // 9️⃣ DESCRIPTION (FINAL)
    if (pending.Description === undefined) {
        const payload = {
            Title: pending.Title,
            TotalHours: pending.TotalHours,
            DueDate: pending.DueDate,
            Project: pending.ProjectId,
            TeamMemberEmployeeId: pending.TeamMemberEmployeeId,
            Status: pending.Status,
            Priority: pending.Priority,
            Visibility: pending.Visibility,
            Tags: JSON.stringify(pending.Tags || []),
            Description: text || "",
            file: pending.file
        };

        pushAIChat(`Creating task "${pending.Title}"...`);
        speak(`Creating task ${pending.Title}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-task-project", {
                detail: payload
            })
        );

        setPendingTask(null);
    }
};

export const startDeleteProjectTaskFlow = (
    setPendingDelete: (data: PendingDeleteTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    titleFromIntent?: string
) => {
    if (titleFromIntent) {
        pushAIChat(`Deleting task "${titleFromIntent}"...`);
        speak(`Deleting task ${titleFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-task-project", {
                detail: { title: titleFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which task should I delete?");
    speak("Which task should I delete?");
};

export const processPendingDeleteProjectTask = (
    text: string,
    pending: PendingDeleteTask,
    setPendingDelete: (data: PendingDeleteTask | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanTitle = text.trim();
    if (!cleanTitle) {
        pushAIChat("Please provide a valid task title.");
        speak("Please provide a valid task title.");
        return;
    }

    pushAIChat(`Deleting task "${cleanTitle}"...`);
    speak(`Deleting task ${cleanTitle}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-task-project", {
            detail: { title: cleanTitle }
        })
    );

    setPendingDelete(null);
};
